// BUILD-046 Owner direction of 2026-10-20 (finish-b, item 3): the page's ONE sort choice - every mode the "Sort:" dropdown lists and the column headings switch
// between - and which of them a view offers.
//
// The choice is one string (kept in localStorage as `mywork-sort`, as before). Three modes that sort by something no column shows keep the legacy sort
// (sortModel.ts, whose declarations stay byte-identical): Oldest, Newest, Project. RECOMMENDED is corrected too (reconcile pass of 2026-10-20): the same date
// buckets and tie-breaks as the legacy Recommended, but decided on the Phoenix calendar day (`recommendedOrder` of taskOrdering.ts), so a task due today is not
// overdue. Every mode that a COLUMN stands for is ordered by taskOrdering.ts as well, so a heading, its arrow, the dropdown label and the rows can never disagree
// and a reverse request is never answered with Recommended. The legacy declarations remain the byte-identical reference for Oldest, Newest and Project, for the
// All / Urgent / High / No due date filters and for the feature-off page:
//
//   mode            column          order                                   aria-sort    stored since
//   title-asc       Task            A-Z                                     ascending    new
//   title-desc      Task            Z-A                                     descending   new
//   assignee-asc    Assigned to     A-Z                                     ascending    new   (Team / Company only)
//   assignee-desc   Assigned to     Z-A                                     descending   new   (Team / Company only)
//   priority        Priority        highest first (Urgent..Low), unset last descending    the earlier "Priority" mode: same name, now with unset last
//   priority-low    Priority        lowest first (Low..Urgent), unset last  ascending     new
//   due-soonest     Due             earliest first, undated last            ascending    the earlier "Due Soonest"   (the column was headed "Task due date" until increment 1a)
//   due-latest      Due             latest first, undated last              descending   the earlier "Due Latest"
//   status-asc      Status          Active, Waiting, Blocked, Completed     ascending    new   (Team / Company only)
//   status-desc     Status          Completed, Blocked, Waiting, Active     descending   new   (Team / Company only)
//
// Assigned to and Status exist as columns only in the Team and Company tables, so only the 'team-work' view offers those four modes; a saved choice a view does not
// offer is shown as Recommended there (the stored choice is not overwritten), exactly as "Project" always was. Sorting covers the LOADED tasks only.
//
// Increment 1a adds the 'issued' view (Assigned by Me): it offers Recommended (the list's own order), Task, Priority and Due (the modes above) and six modes only it has
// (IssuedSortMode: Assigned to, Status / progress, Assigned), ordered by issuedOrdering.ts. The three task views never offer those six, and Assigned by Me never offers the task
// views' Assigned to, Status, Oldest, Newest or Project, so a choice saved in one view is shown as Recommended in a view that does not offer it.
//
// Pure module. Type-only import of the legacy mode names. Runtime import: the column orders of taskOrdering.ts. Erasable TypeScript only.

import type { SortMode, SortView } from './sortModel';
import type { OrderDirection, OrderKey } from './taskOrdering';

export type ColumnSortMode = 'title-asc' | 'title-desc' | 'assignee-asc' | 'assignee-desc' | 'priority' | 'priority-low' | 'due-soonest' | 'due-latest' | 'status-asc' | 'status-desc';
/**
 * BUILD-046 increment 1a: the three modes only the Assigned by Me table adds, beside the ones it shares (Task, Priority, Due). Assigned by Me orders REQUESTS, not tasks, so these are
 * not column modes of taskOrdering.ts: issuedOrdering.ts orders them. "Assigned to" and "Status / progress" are not the Team / Company columns of the same name (a request can
 * name several people, an Anyone request nobody has taken names a group, an Everyone request has a share done), so they are modes of their own and a choice saved in one view
 * can never be read with the other view's meaning.
 */
export type IssuedSortMode = 'responsible-asc' | 'responsible-desc' | 'progress-asc' | 'progress-desc' | 'assigned-newest' | 'assigned-oldest';
// BUILD-046 section C (Owner direction 2026-10-21): 'attention' is a distinct choice, "Needs attention" (attentionModel.ts), offered by the three task views beside Recommended. It is not a column,
// so no heading shows an arrow for it; Recommended is a separate choice and is unchanged. Assigned by Me does not offer it (a request has no follow-up date).
export type PageSortMode = 'recommended' | 'attention' | 'oldest' | 'newest' | 'project' | ColumnSortMode | IssuedSortMode;
/** The views a sort is offered in: the three task views, and Assigned by Me. */
export type PageSortView = SortView | 'issued';

export const DEFAULT_PAGE_SORT: PageSortMode = 'recommended';

/** The modes that stay with the legacy sort (sortModel.ts): Oldest, Newest, Project. (Recommended is `recommendedOrder` of taskOrdering.ts since the reconcile pass.) */
export const LEGACY_SORT_MODES: readonly SortMode[] = Object.freeze(['oldest', 'newest', 'project'] as SortMode[]);

/** What each column mode orders by (taskOrdering.ts). */
export const MODE_ORDER: Readonly<Record<ColumnSortMode, { key: OrderKey; direction: OrderDirection }>> = Object.freeze({
  'title-asc': { key: 'title', direction: 'asc' },
  'title-desc': { key: 'title', direction: 'desc' },
  'assignee-asc': { key: 'assignee', direction: 'asc' },
  'assignee-desc': { key: 'assignee', direction: 'desc' },
  priority: { key: 'priority', direction: 'desc' },
  'priority-low': { key: 'priority', direction: 'asc' },
  'due-soonest': { key: 'due', direction: 'asc' },
  'due-latest': { key: 'due', direction: 'desc' },
  'status-asc': { key: 'status', direction: 'asc' },
  'status-desc': { key: 'status', direction: 'desc' },
});

/** The plain label of each mode, as the dropdown shows it ("Sort: Task A-Z"). */
export const PAGE_SORT_LABELS: Readonly<Record<PageSortMode, string>> = Object.freeze({
  recommended: 'Recommended',
  attention: 'Needs attention',
  'title-asc': 'Task A-Z',
  'title-desc': 'Task Z-A',
  'assignee-asc': 'Assigned to A-Z',
  'assignee-desc': 'Assigned to Z-A',
  priority: 'Priority: highest first',
  'priority-low': 'Priority: lowest first',
  // Increment 1a: the column is headed "Due" (the full name "Task due date" stays in the forms and in the heading's accessible name).
  'due-soonest': 'Due: earliest first',
  'due-latest': 'Due: latest first',
  'status-asc': 'Status: Active to Completed',
  'status-desc': 'Status: Completed to Active',
  oldest: 'Oldest',
  newest: 'Newest',
  project: 'Project',
  // Assigned by Me only (issuedOrdering.ts). The labels say what the order really is for a request that names several people or none.
  'responsible-asc': 'Assigned to A-Z (first name listed)',
  'responsible-desc': 'Assigned to Z-A (first name listed)',
  'progress-asc': 'Status: Unclaimed, then Active to Completed',
  'progress-desc': 'Status: Completed to Active, Unclaimed last',
  'assigned-newest': 'Assigned: newest first',
  'assigned-oldest': 'Assigned: oldest first',
});

/** Every mode, in the order the dropdown lists them (the column modes follow the column order of the table). */
export const PAGE_SORT_MODES: readonly PageSortMode[] = Object.freeze([
  'recommended',
  'attention',
  'title-asc',
  'title-desc',
  'assignee-asc',
  'assignee-desc',
  'priority',
  'priority-low',
  'due-soonest',
  'due-latest',
  'status-asc',
  'status-desc',
  'oldest',
  'newest',
  'project',
] as PageSortMode[]);

/** The four modes whose column only the Team / Company table has. */
const TEAM_ONLY: readonly PageSortMode[] = Object.freeze(['assignee-asc', 'assignee-desc', 'status-asc', 'status-desc'] as PageSortMode[]);

/** The modes only Assigned by Me adds (see `IssuedSortMode`). */
export const ISSUED_SORT_MODES: readonly IssuedSortMode[] = Object.freeze(['responsible-asc', 'responsible-desc', 'progress-asc', 'progress-desc', 'assigned-newest', 'assigned-oldest'] as IssuedSortMode[]);

/**
 * Every mode the Assigned by Me Sort dropdown lists, in the order of its columns: Task, Assigned to, Status / progress, Priority, Due, Assigned. Recommended keeps the order the
 * list has always had (the server's, newest first); Oldest, Newest and Project are the task views' (they sort tasks by data a request row does not carry) and are not offered.
 */
export const ISSUED_VIEW_MODES: readonly PageSortMode[] = Object.freeze([
  'recommended',
  'title-asc',
  'title-desc',
  'responsible-asc',
  'responsible-desc',
  'progress-asc',
  'progress-desc',
  'priority',
  'priority-low',
  'due-soonest',
  'due-latest',
  'assigned-newest',
  'assigned-oldest',
] as PageSortMode[]);

export function isIssuedSortMode(mode: unknown): mode is IssuedSortMode {
  return typeof mode === 'string' && (ISSUED_SORT_MODES as readonly string[]).includes(mode);
}

/** True for any mode that can be saved as the page's choice: the fourteen of the task views and the six only Assigned by Me adds. */
export function isPageSortMode(value: unknown): value is PageSortMode {
  return typeof value === 'string' && ((PAGE_SORT_MODES as readonly string[]).includes(value) || isIssuedSortMode(value));
}

/** True for a mode a column stands for (ordered by `sortTasks` of taskOrdering.ts); false for Recommended (`recommendedOrder`) and the three legacy modes. */
export function isColumnSortMode(mode: PageSortMode): mode is ColumnSortMode {
  return Object.prototype.hasOwnProperty.call(MODE_ORDER, mode);
}

/**
 * The modes the view offers, in dropdown order. My Tasks keeps Project always (the control it always had); Shared and Team offer Project only when a loaded task
 * carries a project. Assigned to and Status are offered by the Team / Company view alone.
 */
export function applicablePageSortModes(view: PageSortView, loaded: readonly { projectDisplayName?: unknown }[] = []): PageSortMode[] {
  // Assigned by Me has its own list (ISSUED_VIEW_MODES): the task views' Assigned to / Status / Oldest / Newest / Project are not offered there.
  if (view === 'issued') return [...ISSUED_VIEW_MODES];
  const withProject = view === 'my-work' || loaded.some((item) => typeof item.projectDisplayName === 'string' && item.projectDisplayName.trim() !== '');
  return PAGE_SORT_MODES.filter((mode) => (mode !== 'project' || withProject) && (view === 'team-work' || !TEAM_ONLY.includes(mode)));
}

/** The mode to apply in a view: the chosen one when it is offered there, else Recommended. The stored choice is not overwritten by this. */
export function effectivePageSortMode(chosen: unknown, view: PageSortView, loaded: readonly { projectDisplayName?: unknown }[] = []): PageSortMode {
  return isPageSortMode(chosen) && applicablePageSortModes(view, loaded).includes(chosen) ? chosen : DEFAULT_PAGE_SORT;
}
